import React, { lazy, useState, useEffect } from 'react';
import PropTypes from 'prop-types';
import { useTranslation } from 'react-i18next';
import { Field } from 'formik';
import moment from 'moment';
import { useHistory } from 'react-router-dom';
import { getDateLocale, getLastQuaters, hasEnabledFeature } from 'globalutils/fmUtils';
import { UI_LOGBOOK_DATE, UI_DATE_FORMAT } from 'globalconfig/constants/static';
import { getPageUrls, getUrls } from 'globalconfig/constants/keys';

const publicUrls = getPageUrls('publicConfig');
const privateUrls = getPageUrls('privateConfig');
const commonUrls = getPageUrls('commonConfig');

const layout = window?.$environment?.CURRENT_LAYOUT;
const Select = lazy(() => import(`globalcomponents/UI/Select/${layout}`));
const Button = lazy(() => import(`globalcomponents/UI/Button/${layout}`));
const RangePicker = lazy(() => import(`globalcomponents/UI/DateRangePicker/${layout}`));
const FormStyle = require(`globalcomponents/UI/Form/${layout}/serverStyle`);
const RadioButton = lazy(() => import(`globalcomponents/UI/Radio/${layout}`));
const Label = lazy(() => import(`globalcomponents/UI/FormLabel/${layout}`));
const urls = getUrls([...publicUrls, ...privateUrls, ...commonUrls]);
export const pageUrls = {
  ...urls,
  notFound: '/notfound'
};

const RenderComponent = ({
  values,
  handleEdit,
  validateForm,
  customProps,
  submitAction
}) => {
  const { i18n } = useTranslation();
  const [suggestions, setSuggestions] = useState([]);
  const history = useHistory();

  useEffect(() => {
    setSuggestions(
      values.vehicleType === 'active'
        ? customProps.activeVehicles
        : customProps.deActivatedVehicles
    );
  }, [values.vehicleType, customProps]);

  useEffect(() => {
    values.id = { label: 'All', value: '' };
  }, [values.vehicleType]);

  const handleExportIfta = (data) => {
    customProps.handleExportIfta({
      id: 'exportIfta',
      data: { ...data }
    });
  };

  const vehicleTypeOptions = [
    {
      label: i18n.t('iftaMileage.activeVehicles'),
      value: 'active'
    },
    {
      label: i18n.t('iftaMileage.deactivateVehicles'),
      value: 'inactive'
    }
  ];

  const renderVehicleSelection = () => (
    <FormStyle.default.FieldsWrapper className="vertical">
      <FormStyle.default.TextWrapper>
        <Field
          name="vehicle"
          onChange={(event) => {
            handleEdit(event, { field: 'id' });
          }}
          id="vehicle"
          as={Select}
          value={values.id}
          suggestions={suggestions}
        />
      </FormStyle.default.TextWrapper>
    </FormStyle.default.FieldsWrapper>
  );

  const renderDateRange = () => (
    <FormStyle.default.FieldsWrapper className="vertical">
      <Label variant="body1" htmlFor="dateRange">
        {`${i18n.t('common.dateFrom')} - ${i18n.t('common.dateTo')}`}
      </Label>
      <FormStyle.default.TextWrapper>
        <Field
          id="dateRange"
          name="dateRange"
          onChange={(date, dateStrings) => {
            handleEdit(dateStrings, { field: 'dateRange' });
            handleEdit(dateStrings[0], { field: 'dateTimeFrom', type: 'date' });
            handleEdit(dateStrings[1], { field: 'dateTimeTo', type: 'date' });
          }}
          onBlur={() => { }} // For ignoring formik error
          predefinedRanges={getLastQuaters()}
          as={RangePicker}
          format={UI_DATE_FORMAT}
          inputReadOnly
          allowClear={false}
          locale={getDateLocale()}
          placeholderText={UI_LOGBOOK_DATE}
          value={values.dateRange.map(ele => moment(ele))}
          showPopperArrow={false}
          showTimeSelect={false}
          maxDate={new Date()}
          showDisabledMonthNavigation
          showMonthDropdown
          showYearDropdown
          dropdownMode="select"
          isClearable
        />
      </FormStyle.default.TextWrapper>
    </FormStyle.default.FieldsWrapper>
  );

  const renderButtons = () => (
    <>
      <FormStyle.default.ButtonsWrapper className="vertical-button">
        <Button
          type="submit"
          label={i18n.t('common.show')}
          onClick={() => {
            validateForm().then((data) => {
              submitAction(data);
            });
          }}
        />
      </FormStyle.default.ButtonsWrapper>
      <FormStyle.default.ButtonsWrapper className="vertical-button reportBtn">
        <Button
          label={i18n.t('iftaMileage.generateReport')}
          onClick={() => {
            validateForm().then(() => {
              customProps.fetchReport(values);
            });
          }}
        />
      </FormStyle.default.ButtonsWrapper>
      {hasEnabledFeature('taxableMilesViewEnabled') && (
        <FormStyle.default.ButtonsWrapper className="vertical-button reportBtn">
          <Button
            label={i18n.t('iftaMileage.updateMiles')}
            onClick={() => {
              history.push({
                pathname: pageUrls.iftaList,
                state: {
                  ...values
                }
              });
            }}
          />
        </FormStyle.default.ButtonsWrapper>
      )}
    </>
  );

  const renderOdometer = () => (
    <FormStyle.default.FieldsWrapper className="odometerWrapper">
      <FormStyle.default.CheckBoxWrapper direction="row">
        <RadioButton
          aria-labelledby="odometer"
          value={values.fromOdometer}
          options={[{ label: i18n.t('iftaMileage.odometer'), value: 'true' }]}
        />
      </FormStyle.default.CheckBoxWrapper>
    </FormStyle.default.FieldsWrapper>
  );

  return (
    <>
      <div className="radiobuttonWrapper">
        <FormStyle.default.FieldsWrapper className="checkWrapper">
          <FormStyle.default.CheckBoxWrapper direction="row" width="100%">
            <RadioButton
              aria-labelledby="subscriptionTier"
              value={values.vehicleType}
              options={vehicleTypeOptions}
              onClick={(event) => {
                handleEdit(event, {
                  field: 'vehicleType',
                  type: 'radio'
                });
              }}
            />
          </FormStyle.default.CheckBoxWrapper>
        </FormStyle.default.FieldsWrapper>
        {renderVehicleSelection()}
      </div>

      <FormStyle.default.FieldsWrapper className="checkWrapper emOnly">
        <FormStyle.default.CheckBoxWrapper direction="row" width="100%">
          <RadioButton
            aria-labelledby="subscriptionTier"
            value={values.vehicleType}
            options={vehicleTypeOptions}
            onClick={(event) => {
              handleEdit(event, {
                field: 'vehicleType',
                type: 'radio'
              });
            }}
          />
        </FormStyle.default.CheckBoxWrapper>
      </FormStyle.default.FieldsWrapper>
      <FormStyle.default.FieldsWrapper className="vertical emOnly">
        {renderVehicleSelection()}
      </FormStyle.default.FieldsWrapper>

      {!customProps?.isOldView && renderOdometer()}

      <div className="dateFieldwrapper">
        {renderDateRange()}
        {renderButtons()}
      </div>

      <FormStyle.default.FieldsWrapper className="vertical emOnly">
        {renderDateRange()}
      </FormStyle.default.FieldsWrapper>

      {customProps?.isOldView && renderOdometer()}

      <FormStyle.default.ButtonsWrapper className="vertical-button emOnly">
        <Button
          type="submit"
          label={i18n.t('common.show')}
          onClick={() => {
            validateForm().then((data) => {
              submitAction(data);
            });
          }}
        />
      </FormStyle.default.ButtonsWrapper>
      <FormStyle.default.ButtonsWrapper className="vertical-button reportBtn emOnly">
        <Button
          label={i18n.t('iftaMileage.generateReport')}
          onClick={() => {
            validateForm().then(() => {
              customProps.fetchReport(values);
            });
          }}
        />
      </FormStyle.default.ButtonsWrapper>
      {hasEnabledFeature('taxableMilesViewEnabled') && (
        <FormStyle.default.ButtonsWrapper className="vertical-button reportBtn">
          <Button
            label={i18n.t('iftaMileage.updateMiles')}
            onClick={() => {
              history.push({
                pathname: pageUrls.iftaList,
                state: {
                  ...values
                }
              });
            }}
          />
        </FormStyle.default.ButtonsWrapper>
      )}
    </>
  );
};

RenderComponent.propTypes = {
  values: PropTypes.object.isRequired,
  handleEdit: PropTypes.func.isRequired,
  validateForm: PropTypes.func.isRequired,
  customProps: PropTypes.object.isRequired,
  submitAction: PropTypes.func.isRequired
};

export default RenderComponent;